import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import type { AlgoOrder } from '@/store/orders/algoOrders/state.ts';
import type { CallOrder } from '@/store/orders/call/state.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import type { TakeProfitOrder } from '@/store/orders/takeProfitOrder/state.ts';
import type { OmsErrors } from '@/types/common.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

type OrderNotionalProps = {
  notionalData:
    | {
        notional: number | undefined;
        notionalCurrencyIndex: CurrencyIndex;
        currency: string | undefined;
      }
    | undefined;
  errors: OmsErrors | undefined;
  validate: (
    updatedProperties:
      | Partial<StopLossOrder['properties']>
      | Partial<TakeProfitOrder['properties']>
      | Partial<CallOrder['properties']>
      | Partial<FixingOrder['properties']>
      | Partial<AlgoOrder['properties']>,
  ) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
  >;
  setIsModifyingField?: (isModifying: boolean) => void;
};

export const OrderNotional = ({ errors, notionalData, validate, setIsModifyingField }: OrderNotionalProps) => {
  const { notional, notionalCurrencyIndex, currency } = notionalData ?? {};
  const track = useTrackEvent();

  const notionalError = errors?.notional;

  const handleNotionalChange = (value: number | undefined) => {
    track('Order action', 'Notional changed');
    validate({
      notional: value,
    });
  };

  const handleCurrencyToggle = () => {
    validate({
      notionalCurrencyIndex: notionalCurrencyIndex === 1 ? 2 : 1,
    });
  };

  return (
    <OrderRow labelId="order.fields.notional.label">
      <OmsFieldErrorWrapper errors={notionalError}>
        <SgAmountInput
          unitBackgroundColor={notionalCurrencyIndex === 2 ? 'warning' : undefined}
          size="sm"
          data-e2e="notional"
          value={notional}
          onChange={handleNotionalChange}
          onFocus={() => setIsModifyingField?.(true)}
          onBlur={() => setIsModifyingField?.(false)}
          unit={currency}
          onUnitClick={handleCurrencyToggle}
          state={isDefined(notionalError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
